import ks from "@shared/kalmstromShared4";
import { IListDefinition, IFieldDefinition, IViewDefinition, IListSettings } from "../DBInterfaces";
import DBState from "../DBState";
import DBOutputSidebar from "../DBOutputSidebar";
import DBStringUtils from "../DBStringUtils";

interface ISiteScriptAction {
    verb: string;
    [key: string]: unknown;
}

interface ISiteScript {
    $schema: string;
    actions: ISiteScriptAction[];
    bindata: Record<string, unknown>;
    version: number;
}

interface IFieldActionResult {
    verb: string;
    fieldType: string;
    displayName: string;
    internalName: string;
    isRequired: boolean;
    addToDefaultView: boolean;
    id?: string;
    choices?: string[];
    richText?: boolean;
    defaultValue?: string;
}

interface IUnsupportedNote {
    list: string;
    note: string;
}

class DBGeneratorSiteScript {

    private static readonly SPLIT_AT: number = 20;

    public static ShowSiteScript = (): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            const appCode: string = DBState.GetAppCode();
            const unsupported: IUnsupportedNote[] = [];
            const actions: ISiteScriptAction[] = DBGeneratorSiteScript.BuildAllActions(appCode, unsupported);

            if (actions.length <= DBGeneratorSiteScript.SPLIT_AT) {
                const siteScript: ISiteScript = { $schema: "schema.json", actions, bindata: {}, version: 1 };
                const formatted: string = JSON.stringify(siteScript, null, 2);
                const filename: string = appCode + "-sitescript.json";
                DBOutputSidebar.ShowOutputSidebar("Site Script", formatted, filename, "application/json");
                DBOutputSidebar.ShowOutputNotes(unsupported);
            } else {
                const files = DBGeneratorSiteScript.SplitIntoFiles(actions, appCode);
                DBOutputSidebar.ShowMultiOutputSidebar("Site Script (" + files.length + " files)", files, unsupported);
            }
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBGeneratorSiteScript.ShowSiteScript");
        }
    };

    public static BuildAllActions = (appCode: string, unsupported: IUnsupportedNote[]): ISiteScriptAction[] => {
        if (ks.kErrorMode === true) { return []; }
        try {
            const actions: ISiteScriptAction[] = [];
            DBGeneratorSiteScript.AddSiteColumnActions(actions, appCode);
            DBGeneratorSiteScript.AddListActions(actions, appCode, unsupported);
            DBGeneratorSiteScript.CollectUnsupportedSettings(unsupported);
            DBGeneratorSiteScript.CollectDataReferences(unsupported);
            return actions;
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBGeneratorSiteScript.BuildAllActions");
            return [];
        }
    };

    public static SplitIntoFiles = (
        actions: ISiteScriptAction[],
        appCode: string
    ): Array<{ name: string; content: string; filename: string; type: string; }> => {
        if (ks.kErrorMode === true) { return []; }
        try {
            const files: Array<{ name: string; content: string; filename: string; type: string; }> = [];
            const labels: string[] = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");
            let offset: number = 0;
            let labelIdx: number = 0;
            while (offset < actions.length) {
                const chunk: ISiteScriptAction[] = actions.slice(offset, offset + DBGeneratorSiteScript.SPLIT_AT);
                const label: string = labels[labelIdx] || String(labelIdx + 1);
                const script: ISiteScript = { $schema: "schema.json", actions: chunk, bindata: {}, version: 1 };
                const filename: string = appCode + "-sitescript-" + label + ".json";
                files.push({
                    name: "Part " + label,
                    content: JSON.stringify(script, null, 2),
                    filename,
                    type: "application/json"
                });
                offset += DBGeneratorSiteScript.SPLIT_AT;
                labelIdx++;
            }
            return files;
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBGeneratorSiteScript.SplitIntoFiles");
            return [];
        }
    };

    public static GenerateSiteScript = (): ISiteScript => {
        if (ks.kErrorMode === true) { return { $schema: "", actions: [], bindata: {}, version: 1 }; }
        try {
            const appCode: string = DBState.GetAppCode();
            const unsupported: IUnsupportedNote[] = [];
            const actions: ISiteScriptAction[] = DBGeneratorSiteScript.BuildAllActions(appCode, unsupported);

            const siteScript: ISiteScript = {
                $schema: "schema.json",
                actions: actions,
                bindata: {},
                version: 1
            };

            return siteScript;
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBGeneratorSiteScript.GenerateSiteScript");
            return { $schema: "", actions: [], bindata: {}, version: 1 };
        }
    };

    public static AddSiteColumnActions = (actions: ISiteScriptAction[], appCode: string): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            const lists: IListDefinition[] = DBState.Lists;
            const addColumnAction = (list: IListDefinition): void => {
                if (list.UseContentType !== true) { return; }
                const group: string = list.ContentTypeGroup || appCode;
                const fieldNames: string[] = Object.keys(list.Properties);
                const addFieldXml = (internalName: string): void => {
                    const field: IFieldDefinition = list.Properties[internalName];
                    const xml: string = DBGeneratorSiteScript.GenerateSiteScriptFieldXml(
                        appCode, list, internalName, field, group
                    );
                    const action: ISiteScriptAction = {
                        verb: "createSiteColumnXml",
                        schemaXml: xml
                    };
                    actions.push(action);
                };
                fieldNames.forEach(addFieldXml);
            };
            lists.forEach(addColumnAction);
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBGeneratorSiteScript.AddSiteColumnActions");
        }
    };

    public static AddListActions = (
        actions: ISiteScriptAction[],
        appCode: string,
        unsupported: IUnsupportedNote[]
    ): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            const lists: IListDefinition[] = DBState.Lists;
            const addListAction = (list: IListDefinition): void => {
                const subactions: ISiteScriptAction[] = [];
                DBGeneratorSiteScript.BuildListSubactions(subactions, list, appCode, unsupported);
                const templateType: number = list.ListTemplate || 100;
                const action: ISiteScriptAction = {
                    verb: "createSPList",
                    listName: list.Title,
                    templateType: templateType,
                    subactions: subactions
                };
                DBGeneratorSiteScript.ApplyListSettings(action, list.Settings);
                actions.push(action);
            };
            lists.forEach(addListAction);
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBGeneratorSiteScript.AddListActions");
        }
    };

    public static ApplyListSettings = (action: ISiteScriptAction, settings: IListSettings | null): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            if (settings === null || settings === undefined) { return; }
            const s: IListSettings = settings;
            if (s.EnableVersioning !== undefined) { action.enableVersioning = s.EnableVersioning; }
            if (s.MajorVersionLimit !== undefined) { action.majorVersionLimit = s.MajorVersionLimit; }
            if (s.EnableMinorVersions !== undefined) { action.enableMinorVersions = s.EnableMinorVersions; }
            if (s.EnableModeration !== undefined) { action.enableModeration = s.EnableModeration; }
            if (s.OnQuickLaunch !== undefined) { action.onQuickLaunch = s.OnQuickLaunch; }
            if (s.EnableAttachments !== undefined) { action.enableAttachments = s.EnableAttachments; }
            if (s.EnableFolderCreation !== undefined) { action.enableFolderCreation = s.EnableFolderCreation; }
            if (s.ContentTypesEnabled !== undefined) { action.contentTypesEnabled = s.ContentTypesEnabled; }
            if (s.ForceCheckout !== undefined) { action.forceCheckout = s.ForceCheckout; }
            if (s.Hidden !== undefined) { action.hidden = s.Hidden; }
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBGeneratorSiteScript.ApplyListSettings");
        }
    };

    public static BuildListSubactions = (
        subactions: ISiteScriptAction[],
        list: IListDefinition,
        appCode: string,
        unsupported: IUnsupportedNote[]
    ): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            DBGeneratorSiteScript.AddFieldSubactions(subactions, list, appCode);
            DBGeneratorSiteScript.AddLookupFields(subactions, list);
            DBGeneratorSiteScript.AddViewSubactions(subactions, list, unsupported);
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBGeneratorSiteScript.BuildListSubactions");
        }
    };

    public static AddFieldSubactions = (
        subactions: ISiteScriptAction[],
        list: IListDefinition,
        appCode: string
    ): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            const fieldNames: string[] = Object.keys(list.Properties);
            const useCT: boolean = list.UseContentType === true;
            const addField = (internalName: string): void => {
                const field: IFieldDefinition = list.Properties[internalName];
                if (field.Type === "lookup" || field.Format === "lookup") { return; }
                if (useCT) {
                    DBGeneratorSiteScript.AddCTFieldSubaction(subactions, list, appCode, internalName, field);
                } else {
                    DBGeneratorSiteScript.AddStandardFieldSubaction(subactions, appCode, list, internalName, field);
                }
            };
            fieldNames.forEach(addField);
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBGeneratorSiteScript.AddFieldSubactions");
        }
    };

    public static AddCTFieldSubaction = (
        subactions: ISiteScriptAction[],
        list: IListDefinition,
        appCode: string,
        internalName: string,
        field: IFieldDefinition
    ): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            const group: string = list.ContentTypeGroup || appCode;
            const xml: string = DBGeneratorSiteScript.GenerateSiteScriptFieldXml(
                appCode, list, internalName, field, group
            );
            const action: ISiteScriptAction = {
                verb: "addSPFieldXml",
                schemaXml: xml
            };
            subactions.push(action);
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBGeneratorSiteScript.AddCTFieldSubaction");
        }
    };

    public static AddStandardFieldSubaction = (
        subactions: ISiteScriptAction[],
        appCode: string,
        list: IListDefinition,
        internalName: string,
        field: IFieldDefinition
    ): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            const result: IFieldActionResult | null = DBGeneratorSiteScript.GenerateSiteScriptField(
                internalName, field
            );
            if (result !== null) {
                subactions.push(result as unknown as ISiteScriptAction);
            } else {
                const xml: string = DBGeneratorSiteScript.GenerateSiteScriptFieldXml(
                    appCode, list, internalName, field, appCode
                );
                if (xml) {
                    subactions.push({ verb: "addSPFieldXml", schemaXml: xml });
                }
            }
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBGeneratorSiteScript.AddStandardFieldSubaction");
        }
    };

    public static AddViewSubactions = (
        subactions: ISiteScriptAction[],
        list: IListDefinition,
        unsupported: IUnsupportedNote[]
    ): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            const views: IViewDefinition[] = list.Views;
            if (views === null || views === undefined) { return; }
            const addView = (view: IViewDefinition): void => {
                const action: ISiteScriptAction = {
                    verb: "addSPView",
                    name: view.Title,
                    viewFields: view.ViewFields || [],
                    query: view.ViewQuery || "",
                    rowLimit: view.RowLimit || 30,
                    isPaged: view.Paged !== false,
                    makeDefault: view.IsDefault === true
                };
                if (view.Scope !== null && view.Scope !== undefined) {
                    action.scope = view.Scope;
                }
                if (view.Hidden === true) {
                    action.hidden = true;
                }
                subactions.push(action);
                DBGeneratorSiteScript.CollectViewUnsupported(unsupported, list.Title, view);
            };
            views.forEach(addView);
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBGeneratorSiteScript.AddViewSubactions");
        }
    };

    public static AddLookupFields = (
        subactions: ISiteScriptAction[],
        list: IListDefinition
    ): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            const fieldNames: string[] = Object.keys(list.Properties);
            const addLookup = (internalName: string): void => {
                const field: IFieldDefinition = list.Properties[internalName];
                const isLookup: boolean = field.Type === "lookup" || field.Format === "lookup";
                if (!isLookup) { return; }
                const action: ISiteScriptAction = {
                    verb: "addSPLookupFieldXml",
                    schemaXml: DBGeneratorSiteScript.BuildLookupXml(internalName, field),
                    targetListName: field.LookupList || ""
                };
                subactions.push(action);
            };
            fieldNames.forEach(addLookup);
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBGeneratorSiteScript.AddLookupFields");
        }
    };

    public static BuildLookupXml = (internalName: string, field: IFieldDefinition): string => {
        if (ks.kErrorMode === true) { return ""; }
        try {
            const displayName: string = DBStringUtils.EscapeXml(field.Title || internalName);
            const guid: string = DBStringUtils.GenerateDeterministicGuid(internalName);
            const lookupField: string = field.LookupField || "Title";
            const required: string = field.Required === true ? "TRUE" : "FALSE";
            const xml: string = '<Field ID="' + guid + '" Name="' + internalName +
                '" DisplayName="' + displayName + '" Type="Lookup"' +
                ' Required="' + required + '" List="' + (field.LookupList || "") +
                '" ShowField="' + lookupField + '" />';
            return xml;
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBGeneratorSiteScript.BuildLookupXml");
            return "";
        }
    };

    public static CollectUnsupportedSettings = (unsupported: IUnsupportedNote[]): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            const lists: IListDefinition[] = DBState.Lists;
            const collectForList = (list: IListDefinition): void => {
                DBGeneratorSiteScript.CollectListUnsupported(unsupported, list);
                const fieldNames: string[] = Object.keys(list.Properties);
                const collectForField = (internalName: string): void => {
                    const field: IFieldDefinition = list.Properties[internalName];
                    DBGeneratorSiteScript.CollectFieldUnsupported(unsupported, list.Title, internalName, field);
                };
                fieldNames.forEach(collectForField);
            };
            lists.forEach(collectForList);
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBGeneratorSiteScript.CollectUnsupportedSettings");
        }
    };

    public static CollectListUnsupported = (
        unsupported: IUnsupportedNote[],
        list: IListDefinition
    ): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            const s: IListSettings | null = list.Settings;
            if (s === null) { return; }
            if (s.DisableGridEditing !== null && s.DisableGridEditing !== undefined) {
                unsupported.push({ list: list.Title, note: "DisableGridEditing not supported in site scripts" });
            }
            if (s.ReadSecurity !== null && s.ReadSecurity !== undefined) {
                unsupported.push({ list: list.Title, note: "ReadSecurity not supported in site scripts" });
            }
            if (s.WriteSecurity !== null && s.WriteSecurity !== undefined) {
                unsupported.push({ list: list.Title, note: "WriteSecurity not supported in site scripts" });
            }
            if (s.ListExperience !== null && s.ListExperience !== undefined) {
                unsupported.push({ list: list.Title, note: "ListExperience not supported in site scripts" });
            }
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBGeneratorSiteScript.CollectListUnsupported");
        }
    };

    public static CollectFieldUnsupported = (
        unsupported: IUnsupportedNote[],
        listTitle: string,
        internalName: string,
        field: IFieldDefinition
    ): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            const prefix: string = "Field '" + internalName + "': ";
            if (field.Format === "currency") {
                unsupported.push({ list: listTitle, note: prefix + "Currency field provisioned as Number (Currency not supported by Site Scripts)" });
            }
            if (field.ShowInDisplayForm === false) {
                unsupported.push({ list: listTitle, note: prefix + "ShowInDisplayForm not supported" });
            }
            if (field.ShowInEditForm === false) {
                unsupported.push({ list: listTitle, note: prefix + "ShowInEditForm not supported" });
            }
            if (field.ShowInNewForm === false) {
                unsupported.push({ list: listTitle, note: prefix + "ShowInNewForm not supported" });
            }
            if (field.CustomFormatter) {
                unsupported.push({ list: listTitle, note: prefix + "CustomFormatter not supported" });
            }
            if (field.ValidationFormula) {
                unsupported.push({ list: listTitle, note: prefix + "ValidationFormula not supported" });
            }
            if (field.EnforceUniqueValues === true) {
                unsupported.push({ list: listTitle, note: prefix + "EnforceUniqueValues not supported" });
            }
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBGeneratorSiteScript.CollectFieldUnsupported");
        }
    };

    public static CollectViewUnsupported = (
        unsupported: IUnsupportedNote[],
        listTitle: string,
        view: IViewDefinition
    ): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            const prefix: string = "View '" + view.Title + "': ";
            if (view.ViewType !== null && view.ViewType !== undefined && view.ViewType !== "") {
                unsupported.push({ list: listTitle, note: prefix + "ViewType not supported" });
            }
            if (view.Aggregations !== null && view.Aggregations !== undefined && view.Aggregations.length > 0) {
                unsupported.push({ list: listTitle, note: prefix + "Aggregations not supported" });
            }
            if (view.CustomFormatter !== null && view.CustomFormatter !== undefined) {
                unsupported.push({ list: listTitle, note: prefix + "CustomFormatter not supported" });
            }
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBGeneratorSiteScript.CollectViewUnsupported");
        }
    };

    public static CollectDataReferences = (unsupported: IUnsupportedNote[]): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            const lists: IListDefinition[] = DBState.Lists;
            const checkData = (list: IListDefinition): void => {
                if (list.Data !== null && list.Data !== undefined && list.Data.length > 0) {
                    unsupported.push({
                        list: list.Title,
                        note: "List data (" + list.Data.length + " rows) cannot be included in site scripts"
                    });
                }
            };
            lists.forEach(checkData);
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBGeneratorSiteScript.CollectDataReferences");
        }
    };

    public static GenerateSiteScriptFieldXml = (
        appCode: string,
        _list: IListDefinition,
        internalName: string,
        field: IFieldDefinition,
        group: string
    ): string => {
        if (ks.kErrorMode === true) { return ""; }
        try {
            const displayName: string = DBStringUtils.EscapeXml(field.Title || internalName);
            const guid: string = DBStringUtils.GenerateDeterministicGuid(appCode + "." + internalName);
            const required: string = field.Required === true ? "TRUE" : "FALSE";
            const camlType: string = DBGeneratorSiteScript.MapFieldToCamlType(field);
            const choices: string = DBGeneratorSiteScript.BuildChoicesXml(field, camlType);
            const fieldBase: string = '<Field ID="' + guid + '"' +
                ' Name="' + internalName + '"' +
                ' DisplayName="' + displayName + '"' +
                ' Type="' + camlType + '"' +
                ' Required="' + required + '"' +
                ' Group="' + DBStringUtils.EscapeXml(group) + '"';
            const xml: string = choices ? fieldBase + ">" + choices + ">" : fieldBase + " />";
            return xml;
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBGeneratorSiteScript.GenerateSiteScriptFieldXml");
            return "";
        }
    };

    public static MapFieldToCamlType = (field: IFieldDefinition): string => {
        if (ks.kErrorMode === true) { return "Text"; }
        try {
            const format: string = field.Format || "";
            const type: string = field.Type || "";
            if (format === "note" || format === "rich-text") { return "Note"; }
            if (format === "date-time" || format === "date") { return "DateTime"; }
            if (format === "url") { return "URL"; }
            if (format === "user") { return "User"; }
            if (format === "integer" || type === "integer") { return "Integer"; }
            if (format === "number" || type === "number") { return "Number"; }
            if (format === "boolean" || type === "boolean") { return "Boolean"; }
            if (format === "geolocation") { return "Geolocation"; }
            if (format === "multichoice") { return "MultiChoice"; }
            if (format === "lookup" || type === "lookup") { return "Lookup"; }
            if (field.Enum !== null && field.Enum !== undefined && field.Enum.length > 0) { return "Choice"; }
            if (field.Items !== null && field.Items !== undefined && field.Items.Enum !== null && field.Items.Enum !== undefined) {
                return "MultiChoice";
            }
            return "Text";
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBGeneratorSiteScript.MapFieldToCamlType");
            return "Text";
        }
    };

    public static BuildChoicesXml = (field: IFieldDefinition, camlType: string): string => {
        if (ks.kErrorMode === true) { return ""; }
        try {
            if (camlType !== "Choice" && camlType !== "MultiChoice") { return ""; }
            let choices: string[] = [];
            if (field.Enum !== null && field.Enum !== undefined) {
                choices = field.Enum;
            } else if (field.Items !== null && field.Items !== undefined && field.Items.Enum !== null && field.Items.Enum !== undefined) {
                choices = field.Items.Enum;
            }
            if (choices.length === 0) { return ""; }
            let xml: string = "<CHOICES>";
            const addChoice = (choice: string): void => {
                xml += "<CHOICE>" + DBStringUtils.EscapeXml(choice) + "</CHOICE>";
            };
            choices.forEach(addChoice);
            xml += "</CHOICES></Field";
            return xml;
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBGeneratorSiteScript.BuildChoicesXml");
            return "";
        }
    };

    public static GenerateSiteScriptField = (
        internalName: string,
        field: IFieldDefinition
    ): IFieldActionResult | null => {
        if (ks.kErrorMode === true) { return null; }
        try {
            const displayName: string = field.Title || internalName;
            const format: string = field.Format || "";
            const type: string = field.Type || "";
            const result: IFieldActionResult = {
                verb: "addSPField",
                fieldType: "Text",
                displayName: displayName,
                internalName: internalName,
                isRequired: field.Required === true,
                addToDefaultView: false
            };
            DBGeneratorSiteScript.ApplyFieldType(result, field, format, type);
            const unsupported: string[] = ["Choice", "MultiChoice", "URL", "Geolocation"];
            if (unsupported.indexOf(result.fieldType as string) >= 0) { return null; }
            if (field.DefaultValue !== null && field.DefaultValue !== undefined) {
                result.defaultValue = String(field.DefaultValue);
            }
            return result;
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBGeneratorSiteScript.GenerateSiteScriptField");
            return null;
        }
    };

    public static ApplyFieldType = (
        result: IFieldActionResult,
        field: IFieldDefinition,
        format: string,
        type: string
    ): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            if (format === "note") {
                result.fieldType = "Note";
                return;
            }
            if (format === "rich-text") {
                result.fieldType = "Note";
                result.richText = true;
                return;
            }
            if (format === "date-time" || format === "date") {
                result.fieldType = "DateTime";
                return;
            }
            if (format === "url") {
                result.fieldType = "URL";
                return;
            }
            if (format === "user") {
                result.fieldType = "User";
                return;
            }
            DBGeneratorSiteScript.ApplyFieldTypeNumericAndOther(result, field, format, type);
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBGeneratorSiteScript.ApplyFieldType");
        }
    };

    public static ApplyFieldTypeNumericAndOther = (
        result: IFieldActionResult,
        field: IFieldDefinition,
        format: string,
        type: string
    ): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            if (format === "integer" || type === "integer" || format === "number" || type === "number") {
                result.fieldType = "Number";
                return;
            }
            if (format === "boolean" || type === "boolean") {
                result.fieldType = "Boolean";
                return;
            }
            if (format === "multichoice") {
                result.fieldType = "MultiChoice";
                if (field.Items !== null && field.Items !== undefined && field.Items.Enum !== null && field.Items.Enum !== undefined) {
                    result.choices = field.Items.Enum;
                }
                return;
            }
            if (format === "geolocation") {
                result.fieldType = "Geolocation";
                return;
            }
            DBGeneratorSiteScript.ApplyFieldTypeDefault(result, field);
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBGeneratorSiteScript.ApplyFieldTypeNumericAndOther");
        }
    };

    public static ApplyFieldTypeDefault = (
        result: IFieldActionResult,
        field: IFieldDefinition
    ): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            if (field.Enum !== null && field.Enum !== undefined && field.Enum.length > 0) {
                result.fieldType = "Choice";
                result.choices = field.Enum;
                return;
            }
            if (field.Items !== null && field.Items !== undefined && field.Items.Enum !== null && field.Items.Enum !== undefined) {
                result.fieldType = "MultiChoice";
                result.choices = field.Items.Enum;
                return;
            }
            result.fieldType = "Text";
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBGeneratorSiteScript.ApplyFieldTypeDefault");
        }
    };
}

export default DBGeneratorSiteScript;
